import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ROUTING_KEY } from '@app/core/enums/routing-key.enum';
import { IBtReadCrumbModel } from '@app/shared/components/bt-breadcrumbs/bt-breadcrumbs.model';

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatTooltipModule],
  selector: 'app-bt-breadcrumb',
  templateUrl: 'bt-breadcrumbs.component.html',
  styleUrls: ['bt-breadcrumbs.component.scss'],
})
export class BtBreadcrumbsComponent implements OnInit, OnDestroy {
  pathArray: Array<IBtReadCrumbModel> = [];

  private destroy$ = new Subject();

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.route.url.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateTo(index: number): void {
    let path = ['/'];
    if (index + 1 === this.pathArray.length) {
      return;
    }
    if (index === 0 && this.pathArray[index].title === 'Places') {
      this.router.navigate(['/']);
      return;
    }
    if (index === 0 && this.pathArray[index].title === 'Guides') {
      this.router.navigate(['/guides']);
      return;
    }
    for (let i = 0; i <= index; i++) {
      if (this.pathArray[i].route) {
        if (this.pathArray[i].route.includes('/')) {
          const routeArray = this.pathArray[i].route.split('/');
          for (let routeElement of routeArray) {
            path.push(routeElement);
          }
        } else {
          path.push(`${this.pathArray[i].route}`);
        }
      }
    }
    this.router.navigate(path);
  }

  back(): void {
    const copyArr = [...this.pathArray];
    copyArr.splice(-1);
    this.router.navigate(
      copyArr?.length ? copyArr.map(item => item.route) : ['/'],
    );
  }

  private setData(): void {
    this.pathArray = [];
    for (const path of this.route.snapshot.pathFromRoot.filter(
      item => !!item.data.page && item.data.page !== ROUTING_KEY.FAKE,
    )) {
      switch (path.data.page) {
        case ROUTING_KEY.EVENT_LIST:
          this.pathArray.push({
            title: 'Events',
            route: path.routeConfig.path,
          });
          break;
        case ROUTING_KEY.EVENT:
          this.pathArray.push({
            title: path.data.eventModel.title,
            route: path.params.eventSlug,
          });
          break;

        case ROUTING_KEY.PLACE_LIST:
          this.pathArray.push({
            title: 'Places',
            route: this.route.snapshot.pathFromRoot.length > 3 ? 'places' : '/',
          });
          break;
        case ROUTING_KEY.PLACE:
          this.pathArray.push({
            title: path.data.placeModel.titleWeb,
            route: path.params.placeSlug,
          });
          break;

        case ROUTING_KEY.GUIDE_LIST:
          this.pathArray.push({
            title: 'Guides',
            route: 'guides',
          });
          break;
        case ROUTING_KEY.GUIDE:
          this.pathArray.push({
            title: path.data.guideModel.title,
            route:
              this.route.snapshot.pathFromRoot.length > 3
                ? path.params.guideSlug
                : null,
          });
          break;
        case ROUTING_KEY.TICKET_LIST:
          this.pathArray.push({
            title: 'Your tickets',
            route: 'your-tickets',
          });
          break;
        case ROUTING_KEY.YOUR_PASSPORT_LIST:
          this.pathArray.push({
            title: 'Your passports',
            route: 'your-passports',
          });
          break;
        case ROUTING_KEY.YOUR_PLANS:
          this.pathArray.push({
            title: 'My saved places',
            route: 'your-plans',
          });
          break;
        case ROUTING_KEY.CATEGORY:
          this.pathArray.push({
            title: path.data.categoryModel.title,
            route: `collections/${path.params.slug}`,
          });
          break;
        case ROUTING_KEY.PASSPORT_LIST:
          this.pathArray.push({
            title: 'Passports',
            route: path.routeConfig.path,
          });
          break;
        case ROUTING_KEY.PASSPORT:
          this.pathArray.push({
            title: path.data.passportModel.title,
            route: path.params.passportSlug,
          });
          break;
      }
    }
  }
}
